import React, { useState, useEffect, useCallback } from 'react';
import { DocumentRecord, DocumentApiConfig } from '../../types/document';
import { DocumentList } from './DocumentList';
import { DocumentEditor } from './DocumentEditor';
import { CreateDocumentModal } from './CreateDocumentModal';
import { useDocumentAPI } from '../../hooks/useDocumentAPI';

interface DocumentSelectorProps {
  visible: boolean;
  selectedText: string;
  onCancel: () => void;
  onConfirm: () => void;
  apiConfig: DocumentApiConfig;
}

export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  visible,
  selectedText,
  onCancel,
  onConfirm,
  apiConfig
}) => {
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<DocumentRecord | null>(null);
  const [documentContent, setDocumentContent] = useState('');
  const [documentHtml, setDocumentHtml] = useState('');
  const [originalFormat, setOriginalFormat] = useState<'html' | 'markdown' | 'text'>('text');
  const [insertPosition, setInsertPosition] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  
  const documentAPI = useDocumentAPI(apiConfig);

  // 改进的格式检测函数
  const detectContentFormat = useCallback((content: string): 'html' | 'markdown' | 'text' => {
    if (!content.trim()) return 'text';
    
    // HTML格式检测 - 更严格的检测
    const htmlTags = /<\/?[a-z][\s\S]*>/i;
    const hasHtmlStructure = /<(html|body|div|p|h[1-6]|ul|ol|li|table|tr|td)\b[^>]*>/i;
    
    if (htmlTags.test(content) && hasHtmlStructure.test(content)) {
      return 'html';
    }
    
    // Markdown格式检测
    const markdownPatterns = [
      /^#{1,6}\s+/m,           // 标题 # ## ###
      /^\*{1,3}[^*]+\*{1,3}/m, // 粗体斜体 *text* **text** ***text***
      /^-{3,}$|^={3,}$/m,      // 分隔线
      /^\s*[-*+]\s+/m,         // 无序列表
      /^\s*\d+\.\s+/m,         // 有序列表
      /^\s*>\s+/m,             // 引用
      /`[^`]+`/,               // 行内代码
      /```[\s\S]*?```/,        // 代码块
      /\[([^\]]+)\]\(([^)]+)\)/, // 链接
      /!\[([^\]]*)\]\(([^)]+)\)/ // 图片
    ];
    
    const markdownScore = markdownPatterns.reduce((score, pattern) => {
      return score + (pattern.test(content) ? 1 : 0);
    }, 0);
    
    // 如果匹配到2个或以上Markdown特征，认为是Markdown
    if (markdownScore >= 2) {
      return 'markdown';
    }
    
    // 如果有任何HTML标签但不是完整HTML结构，可能是混合内容，按HTML处理
    if (htmlTags.test(content)) {
      return 'html';
    }
    
    return 'text';
  }, []);

  // Markdown格式的文本插入函数
  const insertTextInMarkdown = useCallback((markdownContent: string, insertText: string, position: number): string => {
    // 确保position在有效范围内
    const safePosition = Math.max(0, Math.min(position, markdownContent.length));
    
    // 将插入文本转换为Markdown格式
    const insertMarkdown = `\n${insertText}\n`;
    
    // 如果在开头插入
    if (safePosition === 0) {
      return insertMarkdown.trim() + '' + markdownContent;
    }
    
    // 如果在末尾插入
    if (safePosition >= markdownContent.length) {
      return markdownContent + insertMarkdown;
    }
    
    // 在中间插入 - 寻找合适的段落边界
    const beforeText = markdownContent.substring(0, safePosition);
    const afterText = markdownContent.substring(safePosition);
    
    // 寻找最近的段落边界（双换行符）
    const beforeLastParagraph = beforeText.lastIndexOf('\n');
    const afterFirstParagraph = afterText.indexOf('\n');
    
    if (beforeLastParagraph !== -1 && afterFirstParagraph !== -1) {
      // 在段落间插入
      const insertPos = beforeLastParagraph + 2;
      return markdownContent.substring(0, insertPos) + 
             insertMarkdown + 
             markdownContent.substring(insertPos);
    } else if (beforeLastParagraph !== -1) {
      // 在最后一个段落后插入
      return beforeText + insertMarkdown + afterText;
    } else {
      // 简单插入
      return beforeText + insertMarkdown + afterText;
    }
  }, []);

  // 改进的精确位置插入函数
  const insertTextAtPosition = useCallback((content: string, insertText: string, position: number, format: 'html' | 'markdown' | 'text'): string => {
    console.log('[DocumentSelector] 插入文本，格式:', format, '位置:', position);
    
    // 根据格式选择不同的插入策略
    switch (format) {
      case 'markdown':
        return insertTextInMarkdown(content, insertText, position);
        
      case 'html': {
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = content;
        
        if (!content.trim()) {
          return `<p>${insertText.replace(/\n/g, '</p><p>')}</p>`;
        }
        
        const textContent = tempContainer.textContent || tempContainer.innerText || '';
        const safePosition = Math.max(0, Math.min(position, textContent.length));
        const insertHtml = `<p>${insertText.replace(/\n/g, '</p><p>')}</p>`;
        
        if (safePosition === 0) {
          return insertHtml + content;
        }
        
        if (safePosition >= textContent.length) {
          return content + insertHtml;
        }
        
        try {
          // 建立文本位置到DOM元素的映射
          const elementPositionMap: Array<{
            element: Element;
            startPos: number;
            endPos: number;
            tagName: string;
          }> = [];
          
          // 获取所有块级元素
          const blockElements = tempContainer.querySelectorAll('h1, h2, h3, h4, h5, h6, p, div, li, blockquote, pre');
          let currentTextPos = 0;
          
          blockElements.forEach((element) => {
            const elementText = element.textContent || '';
            const startPos = currentTextPos;
            const endPos = currentTextPos + elementText.length;
            
            elementPositionMap.push({
              element,
              startPos,
              endPos,
              tagName: element.tagName.toLowerCase()
            });
            
            currentTextPos = endPos;
          });
          
          console.log('[DocumentSelector] 元素位置映射:', elementPositionMap);
          console.log('[DocumentSelector] 目标位置:', safePosition);
          
          // 找到包含目标位置的元素
          const targetElementInfo = elementPositionMap.find(info => 
            safePosition >= info.startPos && safePosition <= info.endPos
          );
          
          if (targetElementInfo) {
            const { element, tagName } = targetElementInfo;
            console.log('[DocumentSelector] 找到目标元素:', tagName, element);
            
            // 根据元素类型决定插入策略
            let insertAfterElement: Element = element;
            
            // 如果是标题元素，直接在标题后插入
            if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
              // 标题后插入新段落
              const elementHtml = element.outerHTML;
              const beforeHtml = content.substring(0, content.indexOf(elementHtml));
              const afterHtml = content.substring(content.indexOf(elementHtml) + elementHtml.length);
              
              return beforeHtml + elementHtml + insertHtml + afterHtml;
            }
            
            // 如果是段落或其他块级元素，在元素后插入
            if (['p', 'div', 'li', 'blockquote', 'pre'].includes(tagName)) {
              const elementHtml = element.outerHTML;
              const elementIndex = content.indexOf(elementHtml);
              
              if (elementIndex !== -1) {
                const beforeHtml = content.substring(0, elementIndex + elementHtml.length);
                const afterHtml = content.substring(elementIndex + elementHtml.length);
                
                return beforeHtml + insertHtml + afterHtml;
              }
            }
          }
          
          // 如果找不到精确位置，尝试原有的文本节点遍历方法
          const walker = document.createTreeWalker(
            tempContainer,
            NodeFilter.SHOW_TEXT,
            null
          );
          
          let currentLength = 0;
          let targetNode: Node | null = null;
          
          while (walker.nextNode()) {
            const node = walker.currentNode;
            const nodeText = node.textContent || '';
            
            if (currentLength + nodeText.length >= safePosition) {
              targetNode = node;
              break;
            }
            
            currentLength += nodeText.length;
          }
          
          if (targetNode?.parentElement) {
            // 扩展搜索范围，不只是p标签
            let blockElement: HTMLElement | null = targetNode.parentElement;
            const blockTagNames = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'div', 'li', 'blockquote', 'pre'];
            
            while (blockElement && !blockTagNames.includes(blockElement.tagName.toLowerCase())) {
              blockElement = blockElement.parentElement;
            }
            
            if (blockElement) {
              const blockElementHtml = blockElement.outerHTML;
              const elementIndex = content.indexOf(blockElementHtml);
              
              if (elementIndex !== -1) {
                const beforeHtml = content.substring(0, elementIndex + blockElementHtml.length);
                const afterHtml = content.substring(elementIndex + blockElementHtml.length);
                
                console.log('[DocumentSelector] 在块级元素后插入:', blockElement.tagName);
                return beforeHtml + insertHtml + afterHtml;
              }
            }
          }
        } catch (error) {
          console.warn('[DocumentSelector] HTML精确插入失败，使用末尾插入:', error);
        }
        
        console.log('[DocumentSelector] 所有插入方法失败，使用末尾插入');
        return content + insertHtml;
      }
      
      case 'text':
      default: {
        // 纯文本插入
        const safePosition = Math.max(0, Math.min(position, content.length));
        return content.substring(0, safePosition) + 
               '\n' + insertText + '\n' + 
               content.substring(safePosition);
      }
    }
  }, [insertTextInMarkdown]);

  // 加载文档列表
  const loadDocuments = useCallback(async () => {
    if (!documentAPI) return;
    
    try {
      setLoading(true);
      const docs = await documentAPI.getDocuments();
      setDocuments(docs);
      console.log('[DocumentSelector] 文档列表加载完成:', docs);
    } catch (error) {
      console.error('[DocumentSelector] 加载文档列表失败:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [documentAPI]);

  // 改进的文档内容加载函数
  const loadDocumentContent = useCallback(async (selectedDoc: DocumentRecord) => {
    if (!documentAPI) {
      setDocumentContent('');
      setDocumentHtml('');
      setOriginalFormat('text');
      return;
    }

    try {
      setContentLoading(true);
      console.log('[DocumentSelector] 开始加载文档内容:', selectedDoc);
      
      if (!selectedDoc.fileName) {
        console.log('[DocumentSelector] 文档没有关联文件，显示空内容');
        setDocumentContent('');
        setDocumentHtml('');
        setOriginalFormat('text');
        return;
      }

      const response = await documentAPI.getDocumentContent(selectedDoc.fileName);
      const content = response?.content || '';
      
      // 使用改进的格式检测
      const detectedFormat = detectContentFormat(content);
      setOriginalFormat(detectedFormat);
      
      console.log('[DocumentSelector] 检测到文档格式:', detectedFormat);
      
      if (detectedFormat === 'html') {
        setDocumentHtml(content);
        // 从HTML中提取纯文本作为备用
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        setDocumentContent(tempDiv.textContent || tempDiv.innerText || '');
      } else if (detectedFormat === 'markdown') {
        // Markdown格式：保存原始内容，同时转换为HTML用于显示
        setDocumentContent(content);
        // 简单的Markdown到HTML转换（用于编辑器显示）
        const htmlContent = content
          .replace(/^### (.*$)/gm, '<h3>$1</h3>')
          .replace(/^## (.*$)/gm, '<h2>$1</h2>')
          .replace(/^# (.*$)/gm, '<h1>$1</h1>')
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>')
          .replace(/`(.*?)`/g, '<code>$1</code>')
          .replace(/\n/g, '</p><p>')
          .replace(/^(.+)$/gm, '<p>$1</p>')
          .replace(/<p><\/p>/g, '');
        setDocumentHtml(htmlContent || '<p></p>');
      } else {
        // 纯文本格式
        setDocumentContent(content);
        setDocumentHtml(`<p>${content.replace(/\n/g, '</p><p>')}</p>`);
      }
      
      console.log('[DocumentSelector] 文档内容加载完成:', { content, format: detectedFormat });
    } catch (error) {
      console.error('[DocumentSelector] 加载文档内容失败:', error);
      setDocumentContent('');
      setDocumentHtml('');
      setOriginalFormat('text');
    } finally {
      setContentLoading(false);
    }
  }, [documentAPI, detectContentFormat]);

  // 新建文档
  const handleCreateDocument = useCallback(async (name: string) => {
    if (!documentAPI) return;

    try {
      console.log('[DocumentSelector] 开始创建新文档:', name);
      
      // 将选中文本转换为HTML格式
      const htmlContent = `<p>${selectedText.replace(/\n/g, '</p><p>')}</p>`;
      
      // 先创建文件
      const fileResponse = await documentAPI.createDocumentFile(htmlContent, name);
      console.log('[DocumentSelector] 文件创建成功:', fileResponse);

      // 再创建文档记录
      const docRecord = await documentAPI.createDocument({
        name,
        fileName: fileResponse.id
      });
      console.log('[DocumentSelector] 文档记录创建成功:', docRecord);

      setShowCreateModal(false);
      await loadDocuments();
      
      // 选中新创建的文档
      const newDoc: DocumentRecord = {
        id: docRecord.id,
        name: docRecord.name,
        fileName: fileResponse.id,
        date_created: docRecord.date_created,
        date_updated: docRecord.date_updated
      };
      
      setSelectedDocument(newDoc);
      setDocumentContent(selectedText);
      setDocumentHtml(htmlContent);
      setOriginalFormat('html');
      setInsertPosition(selectedText.length);
      
      console.log('[DocumentSelector] 新文档创建并选中完成');
    } catch (error) {
      console.error('[DocumentSelector] 创建文档失败:', error);
      alert('创建文档失败，请重试');
    }
  }, [documentAPI, selectedText, loadDocuments]);

  // 改进的保存文档函数
  const handleSaveDocument = useCallback(async () => {
    if (!documentAPI || !selectedDocument) return;

    try {
      console.log('[DocumentSelector] 开始保存文档:', selectedDocument);
      console.log('[DocumentSelector] 插入位置:', insertPosition);
      console.log('[DocumentSelector] 选中文本:', selectedText);
      console.log('[DocumentSelector] 原始格式:', originalFormat);
      
      // 根据原始格式选择内容源
      const currentContent = originalFormat === 'html' 
        ? (documentHtml || `<p>${documentContent}</p>`)
        : documentContent;
      
      // 使用改进的插入函数
      const newContent = insertTextAtPosition(currentContent, selectedText, insertPosition, originalFormat);

      console.log('[DocumentSelector] 准备保存的内容:', newContent);

      // 如果文档没有关联文件，先创建文件
      if (!selectedDocument.fileName) {
        console.log('[DocumentSelector] 文档无关联文件，先创建文件');
        const fileResponse = await documentAPI.createDocumentFile(newContent, selectedDocument.name);
        
        // 更新文档记录，关联文件
        await documentAPI.updateDocument(selectedDocument.id!, {
          fileName: fileResponse.id
        });
        
        console.log('[DocumentSelector] 文件创建并关联完成');
      } else {
        // 更新已有文件内容
        console.log('[DocumentSelector] 更新已有文件内容');
        await documentAPI.updateDocumentFile(
          selectedDocument.fileName,
          newContent,
          selectedDocument.name
        );
        // 调这个接口是为了记录有更新的时间
        await documentAPI.updateDocument(selectedDocument.id!, {
          fileName: selectedDocument.fileName
        });
      }

      console.log('[DocumentSelector] 文档保存成功');
      onConfirm();
    } catch (error) {
      console.error('[DocumentSelector] 保存文档失败:', error);
      alert('保存文档失败，请重试');
    }
  }, [documentAPI, selectedDocument, documentHtml, documentContent, selectedText, insertPosition, originalFormat, insertTextAtPosition, onConfirm]);

  // 文档选择
  const handleDocumentSelect = useCallback((selectedDoc: DocumentRecord) => {
    console.log('[DocumentSelector] 选择文档:', selectedDoc);
    setSelectedDocument(selectedDoc);
    setInsertPosition(0); // 重置插入位置
    loadDocumentContent(selectedDoc);
  }, [loadDocumentContent]);

  // 处理插入位置更新
  const handleInsertPositionChange = useCallback((position: number) => {
    setInsertPosition(position);
    console.log('[DocumentSelector] 插入位置更新:', position);
  }, []);

  useEffect(() => {
    if (visible) {
      console.log('[DocumentSelector] 弹窗打开，加载文档列表');
      loadDocuments();
    } else {
      console.log('[DocumentSelector] 弹窗关闭，重置状态');
      // 重置状态
      setSelectedDocument(null);
      setDocumentContent('');
      setDocumentHtml('');
      setOriginalFormat('text');
      setInsertPosition(0);
      setShowCreateModal(false);
    }
  }, [visible, loadDocuments]);

  if (!visible) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    width: '90vw',
    height: '80vh',
    maxWidth: '1200px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  };

  const contentStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
    flex: 1,
    minHeight: '400px'
  };

  const leftPanelStyle: React.CSSProperties = {
    width: '300px',
    display: 'flex',
    flexDirection: 'column'
  };

  const rightPanelStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  };

  return (
    <>
      <div style={overlayStyle} onClick={onCancel}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>添加到文档</h3>
            <div style={{ fontSize: '12px', color: '#666' }}>
              选中文本: "{selectedText.length > 50 ? selectedText.substring(0, 50) + '...' : selectedText}"
            </div>
          </div>
          
          <div style={contentStyle}>
            <div style={leftPanelStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0 }}>选择文档</h4>
                <button
                  onClick={() => setShowCreateModal(true)}
                  style={{
                    padding: '4px 12px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  新建文档
                </button>
              </div>
              <DocumentList
                documents={documents}
                selectedDocumentId={selectedDocument?.id}
                onDocumentSelect={handleDocumentSelect}
                loading={loading}
              />
            </div>
            
            <div style={rightPanelStyle}>
              <h4 style={{ margin: '0 0 12px 0' }}>
                文档内容 {selectedDocument ? `- ${selectedDocument.name}` : ''}
                {contentLoading && ' (加载中...)'}
              </h4>
              {selectedDocument ? (
                <DocumentEditor
                  content={documentHtml || documentContent}
                  onContentChange={(html) => setDocumentHtml(html)}
                  onInsertPosition={handleInsertPositionChange}
                  loading={contentLoading}
                  readOnly={true}
                />
              ) : (
                <div style={{ 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: '#999',
                  border: '1px dashed #ddd',
                  borderRadius: '4px'
                }}>
                  请选择一个文档或新建文档
                </div>
              )}
            </div>
          </div>
          
          <div style={{ textAlign: 'right', marginTop: '16px' }}>
            <button
              onClick={onCancel}
              style={{
                marginRight: '8px',
                padding: '8px 20px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer'
              }}
            >
              取消
            </button>
            <button
              onClick={handleSaveDocument}
              disabled={!selectedDocument || contentLoading}
              style={{
                padding: '8px 20px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#007bff',
                color: 'white',
                cursor: (selectedDocument && !contentLoading) ? 'pointer' : 'not-allowed',
                opacity: (selectedDocument && !contentLoading) ? 1 : 0.6
              }}
            >
              确定插入到位置 {insertPosition}
            </button>
          </div>
        </div>
      </div>
      
      <CreateDocumentModal
        visible={showCreateModal}
        onCancel={() => setShowCreateModal(false)}
        onConfirm={handleCreateDocument}
      />
    </>
  );
};